import { FC, useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Outlet, useNavigate, useLoaderData } from 'react-router-dom';

import CommonWrapper from '../components/ui_components/CommonComponentsStructureCss';
import StatSection from '../components/stats_components/StatSection';
import { apiGetStats } from '../components/ui_components/ApiList';
import AltroButtonStats from '../components/stats_components/AltroButtonStats';

interface WrestlerStat {
    id: number;
    name: string;
    avatar_path?: string;
}

interface StatsData {
    bestWrestler?: WrestlerStat;
    mostWinsWrestler?: WrestlerStat;
    mostMatchesWrestler?: WrestlerStat;
    bestWrestlerWoman?: WrestlerStat;
    mostWinsWrestlerWoman?: WrestlerStat;
    mostMatchesWrestlerWoman?: WrestlerStat;
}

const Container = styled.div`
  padding: 30px 5px;

  .stats-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    gap: 2rem;
    padding: 2rem;
  }

  .select-container {
    width: 200px;

    select {
      background-color: rgba(20, 20, 20, 0.9);
      border: 1px solid rgba(163, 27, 27, 0.4);
      border-radius: 8px;
      color: rgba(255, 255, 255, 0.85);
      font-size: 14px;
      padding: 8px 12px;
      width: 100%;
      cursor: pointer;
      transition: border-color 0.2s ease;
      appearance: none;

      &:hover,
      &:focus {
        border-color: rgba(163, 27, 27, 0.8);
        outline: none;
        box-shadow: 0 0 0 2px rgba(163, 27, 27, 0.15);
      }

      option {
        background-color: rgb(20, 20, 20);
        color: rgba(255, 255, 255, 0.85);
      }
    }
  }

  .stats-sections {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.2rem;
    width: 85%;
  }

  .stats-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1.2rem;
    width: 100%;
  }

  .stats-extra {
    width: 100%;
  }
`;

const Title = styled.h1`
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 4px;
  font-size: 28px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.9);
`;


const Stats: FC = () => {
    const navigate = useNavigate();
    const data = useLoaderData() as StatsData;
    const [selectedYear, setSelectedYear] = useState('globale');
    const [stats, setStats] = useState<StatsData>(data || {});
    const [isLoading, setIsLoading] = useState(false);
    const isInitialMount = useRef(true);

    const currentYear = new Date().getFullYear();
    const years: (string | number)[] = ['globale', ...Array.from({ length: currentYear - 2004 }, (_, i) => currentYear - i)];

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            if (selectedYear === 'globale') {
                return;
            }
        }

        const controller = new AbortController();

        const fetchStats = async (): Promise<void> => {
            setIsLoading(true);
            try {
                const response = await apiGetStats(selectedYear) as unknown as StatsData & { error?: string };
                if (controller.signal.aborted) return;
                if (response.error) {
                    console.error('Error in stats response:', response.error);
                } else {
                    setStats(response);
                }
            } catch (error) {
                if (error instanceof Error && error.name !== 'AbortError') {
                    console.error('Error loading stats:', error);
                }
            } finally {
                if (!controller.signal.aborted) setIsLoading(false);
            }
        };
        fetchStats();
        return () => controller.abort();
    }, [selectedYear]);

    const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
        setSelectedYear(e.target.value);
    };

    const handleStatClick = (type: string): void => {
        if (type === 'globale') {
            navigate(`/stats/${type}`);
        } else {
            navigate(`/stats/${type}?year=${selectedYear}`);
        }
    };

    return (
        <CommonWrapper className='CommonWrapperStats'>
            <Container className='containerStats'>
                <Title className='title'>Statistiche</Title>
                <div className="stats-container">
                    <div className="select-container">
                        <select
                            value={selectedYear}
                            onChange={handleYearChange}
                            disabled={isLoading}
                        >
                            {years.map(year => (
                                <option key={year} value={year}>
                                    {year === 'globale' ? 'Globale' : year}
                                </option>
                            ))}
                        </select>
                    </div>

                    {isLoading ? (
                        <div className="text-center">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Caricamento...</span>
                            </div>
                        </div>
                    ) : (
                        <div className="stats-sections">
                            <div className="stats-grid">
                                {stats.bestWrestler && (
                                    <StatSection
                                        title="Miglior Lottatore"
                                        wrestler={stats.bestWrestler as any}
                                        onClick={() => handleStatClick('best-wrestler')}
                                    />
                                )}
                                {stats.mostWinsWrestler && (
                                    <StatSection
                                        title="Lottatore con più Vittorie"
                                        wrestler={stats.mostWinsWrestler as any}
                                        onClick={() => handleStatClick('most-wins-wrestler')}
                                    />
                                )}
                                {stats.mostMatchesWrestler && (
                                    <StatSection
                                        title="Lottatore con più Match"
                                        wrestler={stats.mostMatchesWrestler as any}
                                        onClick={() => handleStatClick('most-matches-wrestler')}
                                    />
                                )}
                                {stats.bestWrestlerWoman && (
                                    <StatSection
                                        title="Miglior Lottatrice"
                                        wrestler={stats.bestWrestlerWoman as any}
                                        onClick={() => handleStatClick('best-wrestler-woman')}
                                    />
                                )}
                                {stats.mostWinsWrestlerWoman && (
                                    <StatSection
                                        title="Lottatrice con più Vittorie"
                                        wrestler={stats.mostWinsWrestlerWoman as any}
                                        onClick={() => handleStatClick('most-wins-wrestler-woman')}
                                    />
                                )}
                                {stats.mostMatchesWrestlerWoman && (
                                    <StatSection
                                        title="Lottatrice con più Match"
                                        wrestler={stats.mostMatchesWrestlerWoman as any}
                                        onClick={() => handleStatClick('most-matches-wrestler-woman')}
                                    />
                                )}
                            </div>

                            <div className="stats-extra">
                                <AltroButtonStats
                                    title="Stats Forum"
                                    onClick={() => handleStatClick('stats-forum')}
                                />
                            </div>
                        </div>
                    )}
                </div>
                <Outlet />
            </Container>
        </CommonWrapper>
    );
};

export default Stats;

export async function loader() {
    try {
        const response = await apiGetStats();
        if (response.error) {
            console.error('Error in stats response:', response.error);
            return {};
        }
        return response;
    } catch (error) {
        console.error('Error loading stats:', error);
        return {};
    }
}
